import { Skeleton, Stack } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import type { Form } from '@/api';
import { FieldEditor } from '@/components/Form/FieldEditor/FieldEditor';
import { DocumentTitle } from '@/components/Ui/DocumentTitle';
import { EmptyText } from '@/components/Ui/EmptyText';
import { Page } from '@/components/Ui/Page';
import { PageHeader } from '@/components/Ui/PageHeader';
import { useRustyState } from '@/state';

export function AccountFormFieldsPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [form, setForm] = useState<Form | null | undefined>(undefined);

  useEffect(() => {
    if (!id) return;
    let live = true;
    useRustyState
      .getState()
      .api.getForm(id)
      .then((res) => live && setForm(res as Form))
      .catch(() => live && setForm(null));
    return () => {
      live = false;
    };
  }, [id]);

  if (form === undefined) {
    return (
      <Stack gap="md" p="md">
        <Skeleton height={56} />
        <Skeleton height={420} />
      </Stack>
    );
  }

  if (!form || !id) {
    return (
      <Page>
        <PageHeader
          title={t('fields.pageTitle')}
          breadcrumb={[{ label: t('forms.pageTitle'), to: '/account/forms' }]}
        />
        <EmptyText>{t('fields.notFound')}</EmptyText>
      </Page>
    );
  }

  return (
    <>
      <DocumentTitle title={`${t('fields.pageTitle')} · ${form.title}`} />
      <FieldEditor
        formId={id}
        formTitle={form.title}
        initialSpec={form.specs ?? ''}
        onSave={async (specs) => {
          await useRustyState.getState().api.updateFormSpecs(id, { specs }, { onSuccess: false });
          void useRustyState.getState().refreshFormsSummary();
        }}
      />
    </>
  );
}
